import React from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from "@/lib/utils";

interface ProfessionalBadgeProps {
  icon: LucideIcon;
  title: string;
  className?: string;
  variant?: 'orange' | 'blue' | 'green' | 'purple';
  textColor?: 'light' | 'dark';
}

export const ProfessionalBadge = ({ icon: Icon, title, className, variant = 'blue', textColor = 'light' }: ProfessionalBadgeProps) => {
  const colorVariants = {
    orange: {
      ribbon: "bg-gradient-to-r from-orange-500 to-amber-500",
      border: "border-orange-400/30",
      glow: "shadow-orange-500/20",
      accent: "from-orange-400/20 to-amber-400/30"
    },
    blue: {
      ribbon: "bg-gradient-to-r from-blue-500 to-cyan-500",
      border: "border-blue-400/30", 
      glow: "shadow-blue-500/20",
      accent: "from-blue-400/20 to-cyan-400/30"
    },
    green: {
      ribbon: "bg-gradient-to-r from-green-500 to-emerald-500",
      border: "border-green-400/30",
      glow: "shadow-green-500/20", 
      accent: "from-green-400/20 to-emerald-400/30"
    },
    purple: {
      ribbon: "bg-gradient-to-r from-purple-500 to-violet-500",
      border: "border-purple-400/30",
      glow: "shadow-purple-500/20",
      accent: "from-purple-400/20 to-violet-400/30"
    }
  };

  const colors = colorVariants[variant];

  return (
    <div className={cn(
      "group relative overflow-visible",
      "bg-white/10 backdrop-blur-sm border border-white/20",
      "transition-all duration-500",
      "hover:bg-white/15 hover:border-white/30",
      "hover:scale-110 hover:-translate-y-2",
      colors.border,
      className
    )}
    style={{
      clipPath: "polygon(0 0, 100% 0, 100% 75%, 50% 100%, 0 75%)",
      paddingTop: "2rem",
      paddingBottom: "2.5rem",
      paddingLeft: "1rem",
      paddingRight: "1rem",
      minHeight: "120px"
    }}>
      {/* Colored Ribbon Banner */}
      <div className={cn(
        "absolute top-0 left-0 right-0 h-8 flex items-center justify-center",
        colors.ribbon,
        "shadow-lg"
      )}
      style={{
        clipPath: "polygon(0 0, 100% 0, 95% 100%, 5% 100%)"
      }}>
        <div className="w-6 h-6 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center border border-white/40">
          <Icon className="w-3 h-3 text-white" />
        </div>
      </div>
      
      {/* Gradient overlay for professional finish */}
      <div className={cn(
        "absolute inset-0 bg-gradient-to-br opacity-40 group-hover:opacity-60 transition-opacity duration-300",
        `bg-gradient-to-br ${colors.accent}`
      )} 
      style={{
        clipPath: "polygon(0 0, 100% 0, 100% 75%, 50% 100%, 0 75%)"
      }} />
      
      {/* Professional glow effect */}
      <div className={cn(
        "absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500",
        `shadow-2xl ${colors.glow}`
      )} 
      style={{
        clipPath: "polygon(0 0, 100% 0, 100% 75%, 50% 100%, 0 75%)"
      }} />
      
      {/* Badge content */}
      <div className="relative z-10 flex flex-col items-center text-center pt-4 pb-2">
        <div className="mb-3">
          <div
            role="text"
            aria-label={title}
            className={cn(
              "text-lg font-bold leading-tight tracking-wide",
              textColor === 'light' ? "text-white" : "text-gray-900"
            )}
          >
            {title.split(' ')[0]}
            {title.split(' ').slice(1).length > 0 && (
              <>
                <br />
                <span className={cn(
                  "text-sm font-medium",
                  textColor === 'light' ? "text-white/90" : "text-gray-800"
                )}>
                  {title.split(' ').slice(1).join(' ')}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Professional badge accent points */}
      <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2">
        <div className="flex gap-1">
          <div className="w-1 h-1 rounded-full bg-gray-600"></div>
          <div className="w-1 h-1 rounded-full bg-gray-400"></div>
          <div className="w-1 h-1 rounded-full bg-gray-600"></div>
        </div>
      </div>
    </div>
  );
};